import { thingsboardService } from './thingsboard';
import { Issue } from '../components/IssueBoardModal';

const DEFAULT_ISSUES: Issue[] = [
  {
    id: '1',
    code: 'ISSUE-101',
    title: '202호 거실 멀티센서 통신 간헐적 끊김',
    description: '거실에 설치된 SEO 멀티센서의 power_current 텔레메트리가 10분 이상 유실되는 현상 발생. 네트워크 재연결 확인 필요.',
    status: 'IN_PROGRESS',
    priority: 'HIGH',
    category: 'NETWORK',
    assignee: '최승구 팀장',
    location: '202호 거실',
    createdAt: '2026-09-27 10:30',
    dueDate: '2026-09-29',
    commentsCount: 3
  },
  {
    id: '2',
    code: 'ISSUE-102',
    title: '전기사용량 텔레메트리 재계산 스크립트 주기적 검증',
    description: 'ThingsBoard 10초 단위 power_current 데이터를 기반으로 호/구역별 월간/주간 통합 통계 재계산 스크립트 실행 및 이상치 삭제 점검.',
    status: 'IN_PROGRESS',
    priority: 'URGENT',
    category: 'SOFTWARE',
    assignee: 'Fizik 엔지니어',
    location: '전체 공간(용인덕성)',
    createdAt: '2026-09-28 09:15',
    dueDate: '2026-09-28',
    commentsCount: 5
  },
  {
    id: '3',
    code: 'ISSUE-103',
    title: '안방 온도 센서 영점 보정 요청',
    description: '안방 실제 온도(22.5°C)와 스마트센서 측정치(25.1°C) 간 2.6°C 오차 발생. 영점 교정 필요.',
    status: 'TODO',
    priority: 'MEDIUM',
    category: 'SENSOR_CALIB',
    assignee: '김민수 기사',
    location: '201호 안방',
    createdAt: '2026-09-28 11:00',
    dueDate: '2026-09-30',
    commentsCount: 1
  },
  {
    id: '4',
    code: 'ISSUE-104',
    title: '주방 스마트 플러그 전력 게이트웨이 교체',
    description: '주방 가전 연결 스마트 플러그 과전류 알림 테스트 및 노후 게이트웨이 하드웨어 점검 완료.',
    status: 'DONE',
    priority: 'LOW',
    category: 'MAINTENANCE',
    assignee: '박지훈 테크니션',
    location: '202호 주방',
    createdAt: '2026-09-25 14:20',
    dueDate: '2026-09-26',
    commentsCount: 2
  },
  {
    id: '5',
    code: 'ISSUE-105',
    title: 'B2B 모바일 화면 씬 에디터 모달 레이아웃 검토',
    description: '스마트 씬 편집 모달에서 장치 선택 목록 반응형 레이아웃 UI 보정 및 테스트 진행.',
    status: 'IN_REVIEW',
    priority: 'MEDIUM',
    category: 'SOFTWARE',
    assignee: 'UI/UX 개발팀',
    location: 'B2B Mobile App',
    createdAt: '2026-09-26 16:45',
    dueDate: '2026-09-29',
    commentsCount: 4
  }
];

let cachedTenantId: string | null = null;

function normalizeIssues(rawList: any[]): Issue[] {
  return rawList.map((item, idx) => ({
    id: String(item.id || Date.now() + idx),
    code: String(item.code || `ISSUE-${100 + idx + 1}`),
    title: String(item.title || '제목 없음'),
    description: String(item.description || ''),
    status: (item.status && ['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'DONE'].includes(item.status)) ? item.status : 'TODO',
    priority: (item.priority && ['URGENT', 'HIGH', 'MEDIUM', 'LOW'].includes(item.priority)) ? item.priority : 'MEDIUM',
    category: (item.category && ['DEVICE_FAULT', 'NETWORK', 'SENSOR_CALIB', 'SOFTWARE', 'MAINTENANCE'].includes(item.category)) ? item.category : 'DEVICE_FAULT',
    assignee: String(item.assignee || '담당자 지정'),
    location: String(item.location || ''),
    createdAt: String(item.createdAt || new Date().toISOString().slice(0, 16).replace('T', ' ')),
    dueDate: item.dueDate ? String(item.dueDate) : undefined,
    commentsCount: typeof item.commentsCount === 'number' ? item.commentsCount : 0
  }));
}

export const issueService = {
  getDefaultIssues(): Issue[] {
    return DEFAULT_ISSUES;
  },

  async resolveTenantId(): Promise<string | null> {
    if (cachedTenantId) return cachedTenantId;
    try {
      const user = await thingsboardService.getUser();
      if (user?.tenantId?.id) {
        cachedTenantId = user.tenantId.id;
        return cachedTenantId;
      }
    } catch (e) {
      console.warn('Could not fetch user tenant info:', e);
    }
    // Fallback known Tenant ID
    return '3ec63df0-d681-11f0-9b2a-09c76e2d22ef';
  },

  async resolveAssetId(customAssetId?: string): Promise<string | null> {
    if (customAssetId && customAssetId !== '00000000-0000-0000-0000-000000000000') {
      return customAssetId;
    }
    const savedId = localStorage.getItem('apartmentAssetId');
    if (savedId && savedId !== '00000000-0000-0000-0000-000000000000') {
      return savedId;
    }
    const envAssetId = import.meta.env.VITE_APARTMENT_ASSET_ID;
    if (envAssetId && envAssetId !== '00000000-0000-0000-0000-000000000000') {
      return envAssetId;
    }

    try {
      const units = await thingsboardService.getAssetsByType('호', 1, 0);
      if (units?.data && units.data.length > 0) {
        return units.data[0].id.id;
      }
    } catch (e) {
      console.warn('Failed to fetch default asset by type:', e);
    }
    return null;
  },

  /**
   * Load issues from ThingsBoard Backend Server Attributes
   * (Tries TENANT level first, then ASSET level)
   */
  async fetchIssues(assetId?: string): Promise<Issue[]> {
    const tenantId = await this.resolveTenantId();
    const targetAssetId = await this.resolveAssetId(assetId);

    // 1. Try TENANT level first
    if (tenantId) {
      try {
        const attrs = await thingsboardService.getEntityAttributes(
          'TENANT',
          tenantId,
          'SERVER_SCOPE',
          ['issues_data']
        );
        const issueAttr = attrs?.find((a: any) => a.key === 'issues_data');
        if (issueAttr && issueAttr.value) {
          let parsed: any[] = [];
          if (typeof issueAttr.value === 'string') {
            try { parsed = JSON.parse(issueAttr.value); } catch (e) { parsed = []; }
          } else if (Array.isArray(issueAttr.value)) {
            parsed = issueAttr.value;
          }

          if (Array.isArray(parsed) && parsed.length > 0) {
            const normalized = normalizeIssues(parsed);
            localStorage.setItem('sed_dashboard_issues', JSON.stringify(normalized));
            return normalized;
          }
        }
      } catch (err) {
        console.warn('Failed to fetch issues from TENANT attribute, falling back to ASSET:', err);
      }
    }

    // 2. Try ASSET level
    if (targetAssetId) {
      try {
        const attrs = await thingsboardService.getEntityAttributes(
          'ASSET',
          targetAssetId,
          'SERVER_SCOPE',
          ['issues_data']
        );

        const issueAttr = attrs?.find((a: any) => a.key === 'issues_data');
        if (issueAttr && issueAttr.value) {
          let parsed: any[] = [];
          if (typeof issueAttr.value === 'string') {
            try { parsed = JSON.parse(issueAttr.value); } catch (e) { parsed = []; }
          } else if (Array.isArray(issueAttr.value)) {
            parsed = issueAttr.value;
          }

          if (Array.isArray(parsed) && parsed.length > 0) {
            const normalized = normalizeIssues(parsed);
            localStorage.setItem('sed_dashboard_issues', JSON.stringify(normalized));
            // Mirror to TENANT as well
            if (tenantId) {
              thingsboardService.saveEntityAttributes('TENANT', tenantId, 'SERVER_SCOPE', {
                issues_data: JSON.stringify(normalized)
              }).catch(() => {});
            }
            return normalized;
          }
        }
      } catch (err) {
        console.warn('Failed to fetch issues from ASSET attribute:', err);
      }
    }

    // 3. Fallback: Initialize with DEFAULT_ISSUES on both TENANT & ASSET
    const local = localStorage.getItem('sed_dashboard_issues');
    let initData = DEFAULT_ISSUES;
    if (local) {
      try {
        const parsed = JSON.parse(local);
        if (Array.isArray(parsed) && parsed.length > 0) {
          initData = normalizeIssues(parsed);
        }
      } catch (e) { /* ignore */ }
    }

    await this.saveIssues(initData, targetAssetId || undefined);
    return initData;
  },

  /**
   * Save issues to ThingsBoard Backend Server Attributes
   * (Persists to both TENANT and ASSET levels)
   */
  async saveIssues(issues: Issue[], assetId?: string): Promise<boolean> {
    const normalized = normalizeIssues(issues);
    // 1. Immediately save to localStorage for instant local cache
    localStorage.setItem('sed_dashboard_issues', JSON.stringify(normalized));

    const tenantId = await this.resolveTenantId();
    const targetAssetId = await this.resolveAssetId(assetId);
    const jsonStr = JSON.stringify(normalized);

    const promises: Promise<any>[] = [];

    if (tenantId) {
      promises.push(
        thingsboardService.saveEntityAttributes(
          'TENANT',
          tenantId,
          'SERVER_SCOPE',
          { issues_data: jsonStr }
        )
      );
    }

    if (targetAssetId) {
      promises.push(
        thingsboardService.saveEntityAttributes(
          'ASSET',
          targetAssetId,
          'SERVER_SCOPE',
          { issues_data: jsonStr }
        )
      );
    }

    if (promises.length === 0) {
      console.warn('No ThingsBoard entity ID available to persist issues remotely');
      return false;
    }

    try {
      const results = await Promise.allSettled(promises);
      const anySuccess = results.some(r => r.status === 'fulfilled');
      if (!anySuccess) {
        console.error('All remote saves failed:', results);
      }
      return anySuccess;
    } catch (err) {
      console.error('Failed to save issues to ThingsBoard backend attribute:', err);
      return false;
    }
  }
};
